/**
 * Générateur — Résoudre algébriquement une équation du premier degré.
 * Attendu ID : 163 | Niveau : 4ème | Domaine : Calcul littéral (O27)
 *
 * Source : doc prof « TANOED EXERCISEUR … 4EME », NOMBRE ET CALCUL, cluster 3,
 * O27. Critère de réussite (image page30) : « résout les équations du type
 * 4x + 2 = 0 ; 5x − 7 = 3 ; 2x + 5 = −x − 4 ». Ex.1 (opération inverse ;
 * Mathieu 1,67 pour 3x = 5), Ex.4/9/10 (deux membres), Ex.6 (résolution de
 * Paul étape par étape).
 *
 * Résolution EXACTE (rationnels de litteral.js). Solutions entières (saisie
 * 1 trou), fractionnaires (saisie 2 trous num/den, jamais de virgule — leçon
 * 4e). Réponses négatives : signe « - » ASCII. Chaque solution est re-vérifiée
 * par substitution dans les deux membres (second chemin, throw).
 *
 * Plan de cartes (validé 2026-07-14, historique) :
 *   resoudre  — résoudre ax + b = c ou ax + b = cx + d, solution entière
 *               (saisie + qcm) + ordonner les étapes (Paul, Ex.6).
 *   fraction  — solution fractionnaire (saisie 2 trous) + Mathieu (valeur
 *               approchée ≠ solution exacte, qcm).
 *   inverse   — quelle opération donne x ? (qcm, Ex.1).
 *   verifier  — « la solution de … est … » vrai/faux.
 *
 * Revue 2026-09-23 (vague 153-163), décisions du coordinateur appliquées en
 * mode auto. Attendu de fin de 4e (Eduscol, attendus de fin d'année, p. 4) :
 * « Il résout algébriquement une équation du premier degré. » Exemple :
 * « Il résout les équations du type : 4x + 2 = 0 ; 5x − 7 = 3 ;
 * 2x + 5 = −x − 4. » (solutions −1/2, 2, −3). Cours : « chacun rentre chez
 * soi », solutions fractionnaires écrites −4/3, −18/7, −1/2, jamais le mot
 * « irréductible ».
 *   - KaTeX partout (règle prof) : équations, lettre x, nombres des étapes et
 *     des options, fractions en \dfrac, décimal en {,} ; moins ASCII,
 *     coefficient 1 masqué, tête négative groupée (helpers de litteral.js).
 *   - Rappel brouillon IMPÉRATIF sur toutes les cartes (RAPPEL_BROUILLON),
 *     juste avant la question : « résoudre algébriquement » se fait par écrit
 *     dans le cours ; la règle est par attendu, la carte « inverse » le porte
 *     aussi.
 *   - « resoudre » saisie : coefficient de x NÉGATIF dans le membre de droite
 *     un tirage sur trois (« −x − 4 » compris, tête groupée) : l'exemple
 *     2x + 5 = −x − 4 était inatteignable ; constantes de valeur absolue au
 *     plus 34 (jusqu'à 54 avant) ; solution entière de −5 à 5 ; « Si la
 *     solution est négative, écris le signe - devant. » en phrase.
 *   - « resoudre » QCM : distracteurs du même réservoir nommé qu'en 162
 *     (oppose, signe_constante, signe_inconnue, membre_droit_sans_x,
 *     division_inversee), entiers, distincts, de valeur absolue au plus 12,
 *     trois tirés parmi ceux qui existent, nouveau tirage borné sinon ; le
 *     rang de la clé parmi les choix triés devient uniforme (les distracteurs
 *     étaient toujours −x0, x0 − 1, x0 + 1 : la clé était le seul « milieu »
 *     de trois entiers consécutifs dans 79,9 % des tirages). Résiduel
 *     mesuré, mode auto, À ARBITRER : comme en 162, oppose est servie dans
 *     97 % des tirages et la clé est dans l'unique paire « nombre et son
 *     opposé » des choix (85 %) ; parade possible hors du réservoir décidé,
 *     la grille oppose × signe_constante (paire 0 %, clé au milieu 75 %).
 *   - « etapes » ordonner : coefficient 1 masqué (« On soustrait x aux deux
 *     membres », 25,3 % de « 1x ») ; lettres et nombres en KaTeX.
 *   - « fraction » saisie : consigne « Donne la solution sous forme d'une
 *     fraction simplifiée au maximum. » (mode auto, À ARBITRER, même choix
 *     qu'en 156 : « irréductible » est un mot de 3e, absent du cours) ; la
 *     forme ax + b = 0 de l'exemple (4x + 2 = 0) un tirage sur quatre ;
 *     fraction empilée à deux trous (fracTrouTex), ligne KaTeX globale ;
 *     « Si la solution est négative, écris le signe - dans le premier trou. ».
 *   - « mathieu » QCM GARDÉ (la carte vient du document du prof, Ex. 1 ;
 *     « valeur exacte / valeur approchée » figure dans les attendus de fin de
 *     4e, partie Nombres : « Il détermine la valeur exacte et une valeur
 *     approchée du périmètre d'un carré d'aire 15 cm² ») : quatre options sur
 *     le même moule, la clé « Non : la solution exacte est 8/3. » (elle était
 *     la seule longue et la seule à dire « valeur approchée » dans 100 % des
 *     tirages), inversion 3/8, soustraction c − a quand c > a sinon
 *     operation_directe a × c, « Oui : 2,67 est la solution exacte. » ;
 *     question « Que penser de l'affirmation de Mathieu ? » (plus de « A-t-il
 *     raison ? »).
 *   - « inverse » QCM : toutes les options disent « aux deux membres » (celle
 *     qui ne le disait pas s'éliminait à sa forme, 50,2 %) ; grille verbe ×
 *     nombre pour ax = c ; tête négative groupée. Les types ax = c et
 *     x + b = c (la moitié des tirages) recoupent l'automatisme 1107, carte
 *     « Quel calcul donne x ? » (mode auto, À ARBITRER : les garder, ou
 *     réserver 163 aux cas propres à la 4e, diviser par un négatif et
 *     x − b = c).
 *   - « verifier » V/F : au moins une ligne fausse d'une famille nommée
 *     (oppose, signe_constante) ; consigne « Vrai ou faux ? » seule (« Résous
 *     chaque équation pour vérifier. » imposait une méthode, remplacer va plus
 *     vite).
 *   - Import inutilisé solveAffine retiré. Contrat des distracteurs exporté
 *     (REGLES_DISTRACTEURS_QCM), codes dans _meta.params.distracteurs.
 * Sept formats (mêmes clés), dont trois QCM.
 *
 * ── Révision 2026-09-24 (demande du prof, validée) ────────────────────────
 *   - « Vrai ou faux : la solution » : TROIS lignes au lieu de quatre (comme
 *     « Ce nombre est-il solution ? » de gen_162 : chaque ligne demande de
 *     résoudre, quatre c'était trop long), une ou deux vraies ; toujours au
 *     moins une fausse de famille nommée ; repli : les deux équations
 *     entières de l'exemple et 3x − 5 = 7.
 *   - « Résoudre une équation du premier degré » : la saisie à trous
 *     « x = ⬚ » (clé resoudre_texte_trou_saisie, SUPPRIMÉE) devient une
 *     SAISIE SIMPLE (resoudre_texte, « Quelle est sa solution ? », « -3 » ou
 *     « x = -3 » justes, liste finie) ; et une SAISIE GUIDÉE
 *     (resoudre_guide_texte_trou_saisie) par la méthode officielle : chaque
 *     ligne fait la même opération sur les deux membres, écrite à droite
 *     (« on ajoute x aux deux membres », « on soustrait 5 … », « on divise les
 *     deux membres par 3 », les mots du format Ordonner), une case par ligne,
 *     |a − c| ≥ 2 pour que la division reste un geste.
 *   - « Une solution fractionnaire » : la saisie à trous reste ; SAISIE GUIDÉE
 *     ajoutée (fraction_guide_texte_trou_saisie) : on soustrait b, on divise
 *     par a (x = N/a, deux cases), « on simplifie au maximum » seulement si la
 *     fraction se simplifie (la simplification partielle existe : 12/8 → 6/4).
 *   Neuf formats désormais.
 */

import {
    randomChoice, randInt, shuffle,
    makeQcm, makeSaisie, makeVraiFaux, makeTexteTrou, makeOrdonner, tex, fracTex, fracTrouTex,
} from '../../../utils';
import { rat, pgcd, texAffine, texNombre, texPoly } from './litteral';

const ATTENDU_ID = 163;

const L_RESOUDRE = 'Résoudre une équation du premier degré';
const L_FRACTION = 'Une solution fractionnaire';
const L_INVERSE = 'Quelle opération donne x ?';
const L_VERIFIER = 'Vrai ou faux : la solution';

const opts = (label) => ({ niveau: '4eme', matiere: 'maths', attendu: ATTENDU_ID, label });

// Rappel brouillon (règle prof 2026-09-23, par attendu) : IMPÉRATIF sur les sept
// cartes. Forme maison du parc (gen_149), juste avant la question.
const RAPPEL_BROUILLON = '<p class="italic text-slate-400">✏️ Pose tes calculs au brouillon.</p>';

const PHRASE_SIGNE = 'Si la solution est négative, écris le signe - devant.';
const PHRASE_SIGNE_CASES = 'Si un nombre est négatif, écris le signe - devant.';

// ── Saisies guidées (2026-09-24, décision prof : « la méthode officielle »)
// Chaque ligne fait LA MÊME opération sur les deux membres, écrite à droite :
// « ← on soustrait 5 aux deux membres », « ← on divise les deux membres par 3 »
// (les mots du format Ordonner). Une ligne entière en KaTeX (mode global).
const ligneGuidee = (ligne, note) => `$${ligne} \\quad \\leftarrow ${note}$`;
/** L'opération qui fait disparaître le terme coef·suffixe d'un membre. */
const noteAnnuler = (coef, suffixe = '') =>
    `\\text{${coef > 0 ? 'on soustrait' : 'on ajoute'} } ${texPoly([[Math.abs(coef), suffixe]])} \\text{ aux deux membres}`;
const noteDiviser = (k) => `\\text{on divise les deux membres par } ${texNombre(k)}`;
const NOTE_SIMPLIFIER = '\\text{on simplifie au maximum}';

const VALEURS = [-5, -4, -3, -2, -1, 1, 2, 3, 4, 5];

/** « {-4} » pour le nombre, en KaTeX. */
const nbTex = (n) => tex(texNombre(n));

// ── Équation ax + b = cx + d (c = 0 : ax + b = d) ──
const latexEq = ({ a, b, c, d }) => `${texAffine(a, b)} = ${c === 0 ? texNombre(d) : texAffine(c, d)}`;

/** Second chemin : x0 vérifie l'équation (substitution dans les deux membres) et elle n'a qu'une solution. */
const verifierEntiere = (e) => {
    if (e.a === e.c || e.a * e.x0 + e.b !== e.c * e.x0 + e.d) throw new Error(`gen_163 : ${latexEq(e)} et ${e.x0}`);
    return e;
};

// ═════════ Carte 1 — Résoudre (saisie / qcm / ordonner) ═════════

/**
 * Équation à solution entière x0 de −5 à 5 : ax + b = C (un tiers), ax + b =
 * cx + d avec c positif (un tiers), avec c NÉGATIF de −5 à −1 (un tiers :
 * « 2x + 5 = −x − 4 ») ; constantes de valeur absolue au plus 34.
 */
const tirerEquationEntiere = () => {
    // la forme d'abord, les paramètres ensuite : le rejet des constantes
    // au-delà de 34 frappe surtout c négatif, il ne doit pas changer les parts
    const forme = randomChoice(['une', 'deux_pos', 'deux_neg']);
    for (let essai = 0; essai < 80; essai++) {
        const x0 = randomChoice(VALEURS);
        const a = randInt(2, 9);
        const b = randInt(1, 9) * randomChoice([1, -1]);
        const c = forme === 'une' ? 0
            : forme === 'deux_neg' ? randInt(-5, -1)
                : randomChoice([1, 2, 3, 4, 5, 6, 7, 8].filter((k) => k !== a));
        const d = (a - c) * x0 + b;
        if (Math.abs(d) > 34) continue;
        return verifierEntiere({ forme, a, b, c, d, x0 });
    }
    // Repli déterministe vérifié : l'exemple de réussite 2x + 5 = −x − 4, solution −3.
    return verifierEntiere({ forme: 'deux_neg', a: 2, b: 5, c: -1, d: -4, x0: -3 });
};

// Saisie simple (2026-09-24, décision prof) : la solution seule, « -3 » ou
// « x = -3 » (liste finie, comparaison d'expression). Remplace « x = ⬚ ».
const genResoudreSaisie = () => {
    const e = tirerEquationEntiere();
    const texte = RAPPEL_BROUILLON
        + `<p><b>Résous l'équation ${tex(latexEq(e))}. Quelle est sa solution ?</b></p>`
        + `<p>${PHRASE_SIGNE}</p>`;
    const payload = makeSaisie(tex(`x = ${texNombre(e.x0)}`), texte, {
        ...opts(L_RESOUDRE), strict: true, comparaison: 'expression',
        reponses_acceptees: [String(e.x0), `x = ${e.x0}`],
    });
    payload._meta.params = { ...e, eq: latexEq(e) };
    return payload;
};

// Saisie guidée (2026-09-24) : on soustrait cx, puis b, aux deux membres, puis
// on divise par a − c ; une seule case par ligne, aucun échange de cases ne
// donne une autre réponse juste. |a − c| ≥ 2 : la division reste un vrai geste.
const genResoudreGuide = () => {
    let e = null;
    for (let essai = 0; essai < 50 && !e; essai++) {
        const t = tirerEquationEntiere();
        if (Math.abs(t.a - t.c) >= 2) e = t;
    }
    // Repli déterministe vérifié : l'exemple de réussite 2x + 5 = −x − 4.
    if (!e) e = verifierEntiere({ forme: 'deux_neg', a: 2, b: 5, c: -1, d: -4, x0: -3 });
    const { a, b, c, d, x0 } = e;
    const k = a - c;
    // second chemin : x0 vérifie chaque ligne, cases remplies
    if (k * x0 + b !== d || k * x0 !== d - b) throw new Error(`gen_163 : guide ${latexEq(e)}`);
    const lignes = [`$${latexEq(e)}$`];
    if (c !== 0) lignes.push(ligneGuidee(`{{${k}}}x ${b < 0 ? '-' : '+'} ${Math.abs(b)} = ${texNombre(d)}`, noteAnnuler(c, 'x')));
    lignes.push(ligneGuidee(`${texPoly([[k, 'x']])} = {{${d - b}}}`, noteAnnuler(b)));
    lignes.push(ligneGuidee(`x = {{${x0}}}`, noteDiviser(k)));
    const texte = RAPPEL_BROUILLON
        + `<p><b>Complète les étapes pour résoudre l'équation ${tex(latexEq(e))}.</b></p>`
        + `<p>${PHRASE_SIGNE_CASES}</p>`;
    const payload = makeTexteTrou(lignes.join('\n'), texte, { ...opts(L_RESOUDRE), mode: 'saisie' });
    payload._meta.params = { ...e, eq: latexEq(e) };
    return payload;
};

// QCM : le réservoir d'erreurs nommées de 162, relues sur ax + b = cx + d.
const reservoirResoudre = (a, b, c, d, x0) => [
    { code: 'oppose', num: -x0, den: 1 },                  // le signe de la solution perdu
    { code: 'signe_constante', num: d + b, den: a - c },   // b passé de l'autre côté sans changer de signe
    { code: 'signe_inconnue', num: d - b, den: a + c },    // cx passé de l'autre côté sans changer de signe
    { code: 'membre_droit_sans_x', num: d - b, den: a },   // cx oublié
    { code: 'division_inversee', num: a - c, den: d - b }, // (a − c)/(d − b) = 1/x0 : jamais entière hors la clé
];

/** Valeurs entières distinctes du réservoir, ≠ x0, |v| ≤ borne, dans un ordre tiré au hasard. */
const distracteursNommes = (a, b, c, d, x0, borne) => {
    const vus = new Set([x0]);
    const out = [];
    shuffle(reservoirResoudre(a, b, c, d, x0)).forEach(({ code, num, den }) => {
        if (den === 0 || num % den !== 0) return;
        const valeur = num / den + 0;                       // + 0 : jamais « -0 »
        if (vus.has(valeur) || Math.abs(valeur) > borne) return;
        vus.add(valeur);
        out.push({ code, valeur });
    });
    return out;
};

const assemblerResoudreQcm = (e, distracteurs) => {
    const choix = [e.x0, ...distracteurs.map((f) => f.valeur)].map(nbTex);
    const payload = makeQcm(choix, choix[0],
        `${RAPPEL_BROUILLON}<p><b>Quelle est la solution de l'équation ${tex(latexEq(e))} ?</b></p>`, opts(L_RESOUDRE));
    payload._meta.params = { ...e, distracteurs: distracteurs.map((f) => f.code) };
    return payload;
};

const genResoudreQcm = () => {
    for (let essai = 0; essai < 300; essai++) {
        const a = randInt(2, 9);
        const c = randomChoice([1, 2, 3, 4, 5, 6, 7, 8].filter((k) => k !== a));
        const x0 = randomChoice(VALEURS);
        const b = randInt(1, 9) * randomChoice([1, -1]);
        const e = verifierEntiere({ a, b, c, d: (a - c) * x0 + b, x0 });
        const distracteurs = distracteursNommes(e.a, e.b, e.c, e.d, e.x0, 12);
        if (distracteurs.length < 3) continue;
        return assemblerResoudreQcm(e, distracteurs.slice(0, 3));
    }
    // Repli déterministe vérifié : 4x + 2 = 2x + 8, solution 3 ; oppose −3,
    // signe_constante (8 + 2)/2 = 5, signe_inconnue (8 − 2)/6 = 1.
    const e = verifierEntiere({ a: 4, b: 2, c: 2, d: 8, x0: 3 });
    const distracteurs = distracteursNommes(4, 2, 2, 8, 3, 12);
    if (distracteurs.length < 3) throw new Error('gen_163 : repli resoudre_qcm');
    return assemblerResoudreQcm(e, distracteurs.slice(0, 3));
};

const genEtapesOrdonner = () => {
    // a x + b = c x + d, a − c ≥ 2, b > 0, solution entière x0 ; les x d'abord,
    // les nombres ensuite (chaque résultat affiché rend l'ordre unique)
    const x0 = randomChoice(VALEURS);
    const c = randInt(1, 4);
    const a = c + randInt(2, 5);
    const b = randInt(1, 9);
    const e = verifierEntiere({ a, b, c, d: (a - c) * x0 + b, x0 });
    const k = a - c;
    if (k * x0 !== e.d - b) throw new Error('gen_163 : étapes');
    const etape1 = `On soustrait ${tex(texPoly([[c, 'x']]))} aux deux membres, ce qui donne ${tex(`${texAffine(k, b)} = ${texNombre(e.d)}`)}.`;
    const etape2 = `On soustrait ${tex(String(b))} aux deux membres, ce qui donne ${tex(`${texPoly([[k, 'x']])} = ${texNombre(e.d - b)}`)}.`;
    const etape3 = `On divise les deux membres par ${tex(String(k))}, ce qui donne ${tex(`x = ${texNombre(x0)}`)}.`;
    const texte = `<p>Voici, dans le désordre, les étapes de la résolution de l'équation ${tex(latexEq(e))}.</p>`
        + RAPPEL_BROUILLON
        + '<p><b>Remets les étapes dans l\'ordre.</b></p>';
    const payload = makeOrdonner([etape1, etape2, etape3], texte, opts(L_RESOUDRE));
    payload._meta.params = { ...e };
    return payload;
};

// ═════════ Carte 2 — Solution fractionnaire & Mathieu ═════════

const assemblerFraction = (a, b, N) => {
    const d = N + b;                                        // ax + b = d  ⇒  x = N/a
    const x = rat(N, a);                                    // simplifiée au maximum, q ≥ 2
    // second chemin : a·p/q + b = d, fraction irréductible, dénominateur ≥ 2
    if (a * x.p + b * x.q !== d * x.q || pgcd(x.p, x.q) !== 1 || x.q < 2) throw new Error(`gen_163 : fraction ${a} ${b} ${N}`);
    const eq = `${texAffine(a, b)} = ${texNombre(d)}`;
    const texte = RAPPEL_BROUILLON
        + `<p><b>Résous l'équation ${tex(eq)}.</b></p>`
        + '<p>Donne la solution sous forme d\'une fraction simplifiée au maximum. '
        + 'Si la solution est négative, écris le signe - dans le premier trou.</p>';
    const phrase = `$x = ${fracTrouTex(`{{${x.p}}}`, `{{${x.q}}}`)}$`;
    const payload = makeTexteTrou(phrase, texte, { ...opts(L_FRACTION), mode: 'saisie' });
    payload._meta.params = { a, b, d, x: `${x.p}/${x.q}`, formeZero: d === 0 };
    return payload;
};

/** ax + b = d à solution N/a non entière (d = N + b). */
const tirerFraction = () => {
    // la forme ax + b = 0 de l'exemple (4x + 2 = 0) un tirage sur quatre,
    // tirée AVANT les paramètres pour que les rejets ne changent pas la part
    const zero = randInt(1, 4) === 1;
    for (let essai = 0; essai < 80; essai++) {
        const a = randInt(2, 9);
        const b = randInt(1, 9) * randomChoice([1, -1]);
        const N = zero ? -b : randInt(-15, 15);
        if (N === 0 || N % a === 0 || (!zero && N + b === 0)) continue;   // solution non entière
        return { a, b, N };
    }
    // Repli déterministe vérifié : l'exemple de réussite 4x + 2 = 0, solution −1/2.
    return { a: 4, b: 2, N: -2 };
};

const genFractionSaisie = () => {
    const { a, b, N } = tirerFraction();
    return assemblerFraction(a, b, N);
};

// Saisie guidée (2026-09-24, décision prof) : la même méthode jusqu'à la
// fraction ; « on simplifie au maximum » seulement si elle se simplifie.
const genFractionGuide = () => {
    const { a, b, N } = tirerFraction();
    const d = N + b;
    const x = rat(N, a);
    // second chemin : a·p/q + b = d, fraction simplifiée au maximum
    if (a * x.p + b * x.q !== d * x.q || pgcd(x.p, x.q) !== 1 || x.q < 2) throw new Error(`gen_163 : fraction guidée ${a} ${b} ${N}`);
    const eq = `${texAffine(a, b)} = ${texNombre(d)}`;
    const lignes = [
        `$${eq}$`,
        ligneGuidee(`${a}x = {{${N}}}`, noteAnnuler(b)),
        ligneGuidee(`x = ${fracTrouTex(`{{${N}}}`, `{{${a}}}`)}`, noteDiviser(a)),
    ];
    if (x.q !== a) lignes.push(ligneGuidee(`x = ${fracTrouTex(`{{${x.p}}}`, `{{${x.q}}}`)}`, NOTE_SIMPLIFIER));
    const texte = RAPPEL_BROUILLON
        + `<p><b>Complète les étapes pour résoudre l'équation ${tex(eq)}.</b></p>`
        + `<p>${PHRASE_SIGNE_CASES} Pour une fraction, dans le premier trou.</p>`;
    const payload = makeTexteTrou(lignes.join('\n'), texte, { ...opts(L_FRACTION), mode: 'saisie' });
    payload._meta.params = { a, b, d, x: `${x.p}/${x.q}`, simplifiee: x.q !== a };
    return payload;
};

// Mathieu : « 2,67 est solution de 3x = 8 ». Quatre options sur le même moule.
const decimalTex = (s) => s.replace(',', '{,}');
const approche = (a, c) => {
    const centiemes = Math.round((100 * c) / a);
    const ent = Math.floor(centiemes / 100);
    const dec = String(centiemes % 100).padStart(2, '0').replace(/0$/, '');
    return dec ? `${ent},${dec}` : String(ent);
};

const assemblerMathieu = (a, c) => {
    const app = approche(a, c);
    // second chemin : l'approximation n'est jamais la solution exacte (a, premier
    // avec c, a toujours un facteur premier autre que 2 et 5)
    const [ent, dec = ''] = app.split(',');
    const appNum = Number(ent) * 10 ** dec.length + Number(dec || 0);
    if (appNum * a === c * 10 ** dec.length || pgcd(a, c) !== 1 || c % a === 0) throw new Error(`gen_163 : mathieu ${a} ${c}`);
    const troisieme = c > a
        ? { code: 'soustraction', texte: `Non : la solution exacte est ${tex(String(c - a))}.` }
        : { code: 'operation_directe', texte: `Non : la solution exacte est ${tex(String(a * c))}.` };
    const options = [
        { code: null, texte: `Non : la solution exacte est ${fracTex(c, a)}.` },
        { code: 'inversion', texte: `Non : la solution exacte est ${fracTex(a, c)}.` },
        troisieme,
        { code: 'approchee_prise_pour_exacte', texte: `Oui : ${tex(decimalTex(app))} est la solution exacte.` },
    ];
    const texte = `<p>Mathieu affirme que ${tex(decimalTex(app))} est solution de l'équation ${tex(`${a}x = ${c}`)}.</p>`
        + RAPPEL_BROUILLON
        + '<p><b>Que penser de l\'affirmation de Mathieu ?</b></p>';
    const payload = makeQcm(options.map((o) => o.texte), options[0].texte, texte, opts(L_FRACTION));
    payload._meta.params = { a, c, app, distracteurs: options.slice(1).map((o) => o.code) };
    return payload;
};

const genMathieuQcm = () => {
    for (let essai = 0; essai < 80; essai++) {
        const a = randomChoice([3, 6, 7, 9, 11]);
        const c = randInt(2, 22);
        // pgcd(a, c) = 1 : la solution exacte c/a est déjà simplifiée au maximum
        if (c % a === 0 || pgcd(a, c) !== 1) continue;
        return assemblerMathieu(a, c);
    }
    // Repli déterministe vérifié : 3x = 8, Mathieu dit 2,67.
    return assemblerMathieu(3, 8);
};

// ═════════ Carte 3 — Quelle opération donne x ? (Ex.1) ═════════
//
// Chaque option est une opération appliquée aux deux membres ; appliquée au
// membre de gauche (coef·x + const), seule la clé laisse x seul (second chemin).

const OPERATIONS = {
    div: (k) => ({ texte: `Diviser les deux membres par ${nbTex(k)}`, f: ([co, cs]) => [rat(co.p, co.q * k), rat(cs.p, cs.q * k)] }),
    mul: (k) => ({ texte: `Multiplier les deux membres par ${nbTex(k)}`, f: ([co, cs]) => [rat(co.p * k, co.q), rat(cs.p * k, cs.q)] }),
    aj: (k) => ({ texte: `Ajouter ${nbTex(k)} aux deux membres`, f: ([co, cs]) => [co, rat(cs.p + k * cs.q, cs.q)] }),
    so: (k) => ({ texte: `Soustraire ${nbTex(k)} aux deux membres`, f: ([co, cs]) => [co, rat(cs.p - k * cs.q, cs.q)] }),
};
const laisseXSeul = ([co, cs]) => co.p === 1 && co.q === 1 && cs.p === 0;

const genInverseQcm = () => {
    const type = randomChoice(['mul', 'mulNeg', 'plus', 'moins']);
    let eq; let gauche; let options;
    if (type === 'mul') {
        // grille verbe × nombre : diviser / multiplier, par a / par c
        const a = randInt(2, 9);
        const c = randInt(2, 9) * a;
        eq = `${a}x = ${c}`;
        gauche = [rat(a), rat(0)];
        options = [
            { ...OPERATIONS.div(a), code: null },
            { ...OPERATIONS.mul(a), code: 'operation_directe' },
            { ...OPERATIONS.div(c), code: 'inversion' },
            { ...OPERATIONS.mul(c), code: 'les_deux' },
        ];
    } else if (type === 'mulNeg') {
        const a = randInt(2, 9);
        const c = randInt(2, 9);
        eq = `${texPoly([[-a, 'x']])} = ${c}`;
        gauche = [rat(-a), rat(0)];
        options = [
            { ...OPERATIONS.div(-a), code: null },
            { ...OPERATIONS.div(a), code: 'signe' },
            { ...OPERATIONS.mul(-a), code: 'operation_directe' },
            { ...OPERATIONS.aj(a), code: 'lecture_additive' },
        ];
    } else {
        const b = randInt(2, 9);
        const c = randInt(1, 12);
        const plus = type === 'plus';
        eq = `x ${plus ? '+' : '-'} ${b} = ${c}`;
        gauche = [rat(1), rat(plus ? b : -b)];
        options = [
            { ...(plus ? OPERATIONS.so(b) : OPERATIONS.aj(b)), code: null },
            { ...(plus ? OPERATIONS.aj(b) : OPERATIONS.so(b)), code: 'operation_directe' },
            { ...OPERATIONS.div(b), code: 'lecture_multiplicative' },
            { ...OPERATIONS.mul(b), code: 'les_deux' },
        ];
    }
    const justes = options.filter((o) => laisseXSeul(o.f(gauche)));
    if (justes.length !== 1 || justes[0].code !== null) throw new Error(`gen_163 : inverse ${eq}`);
    const texte = RAPPEL_BROUILLON
        + `<p><b>Pour résoudre l'équation ${tex(eq)}, quelle opération applique-t-on aux deux membres pour obtenir ${tex('x')} ?</b></p>`;
    const payload = makeQcm(options.map((o) => o.texte), options[0].texte, texte, opts(L_INVERSE));
    payload._meta.params = { type, eq, distracteurs: options.slice(1).map((o) => o.code) };
    return payload;
};

// ═════════ Carte 4 — Vrai/faux : la solution ═════════

/** Valeurs fausses de famille nommée pour une équation (entières, ≠ x0). */
const faussesNommees = (e) => {
    const out = [{ code: 'oppose', valeur: -e.x0 }];
    const num = e.d + e.b;
    const den = e.a - e.c;
    if (num % den === 0 && num / den !== e.x0 && num / den !== -e.x0) out.push({ code: 'signe_constante', valeur: num / den + 0 });
    return out;
};

const ligneVf = (e, valeur) => tex(`\\text{La solution de } ${latexEq(e)} \\text{ est } ${texNombre(valeur)}`);

const assemblerVerifier = (items, verites) => {
    const lignes = items.map((it) => ligneVf(it.e, it.valeur));
    if (new Set(lignes).size !== items.length) throw new Error('gen_163 : verifier, lignes en double');
    // second chemin : la valeur affichée vérifie l'équation ssi la ligne est vraie
    items.forEach((it, i) => {
        verifierEntiere(it.e);
        const ok = it.e.a * it.valeur + it.e.b === it.e.c * it.valeur + it.e.d;
        if (ok !== verites[i]) throw new Error(`gen_163 : ligne ${lignes[i]}`);
    });
    const payload = makeVraiFaux(lignes, verites.map((v) => (v ? 0 : 1)),
        `${RAPPEL_BROUILLON}<p><b>Vrai ou faux ?</b></p>`, opts(L_VERIFIER));
    payload._meta.params = {
        lignes: items.map((it) => ({ eq: latexEq(it.e), valeur: it.valeur, code: it.code })),
        distracteurs: items.filter((it) => it.code).map((it) => it.code),
    };
    return payload;
};

const genVerifierVf = () => {
    for (let essai = 0; essai < 80; essai++) {
        // Trois lignes (prof 2026-09-24 : quatre, c'était trop long), une ou deux vraies.
        const nbVrai = randInt(1, 2);
        const verites = shuffle([...Array(nbVrai).fill(true), ...Array(3 - nbVrai).fill(false)]);
        const iFamille = verites.indexOf(false);             // au moins une fausse de famille nommée
        const items = verites.map((vraie, i) => {
            const e = tirerEquationEntiere();
            if (vraie) return { e, valeur: e.x0, code: null };
            const nommees = faussesNommees(e);
            const voisins = [-2, -1, 1, 2].map((k) => e.x0 + k).filter((w) => w !== 0 && w !== -e.x0)
                .map((w) => ({ code: 'voisin', valeur: w }));
            const f = randomChoice(i === iFamille ? nommees : [...nommees, ...voisins]);
            return { e, valeur: f.valeur, code: f.code };
        });
        if (new Set(items.map((it) => ligneVf(it.e, it.valeur))).size < 3) continue;
        return assemblerVerifier(items, verites);
    }
    // Repli déterministe vérifié : les deux équations entières de l'exemple de
    // réussite (4x + 2 = 0 est dans « fraction ») et 3x − 5 = 7 ; deux vraies,
    // une fausse de famille nommée.
    return assemblerVerifier([
        { e: { a: 5, b: -7, c: 0, d: 3, x0: 2 }, valeur: 2, code: null },
        { e: { a: 2, b: 5, c: -1, d: -4, x0: -3 }, valeur: 3, code: 'oppose' },
        { e: { a: 3, b: -5, c: 0, d: 7, x0: 4 }, valeur: 4, code: null },
    ], [true, false, true]);
};

// ═════════ Contrat des distracteurs (données pures, jamais lues à l'exécution) ═════════

export const REGLES_DISTRACTEURS_QCM = {
    invariants_communs: [
        'chaque distracteur est une erreur NOMMÉE d\'élève (codes dans _meta.params.distracteurs), jamais une option inventée à l\'assemblage',
        'la clé est re-vérifiée par substitution dans les deux membres (throw) ; toutes les options d\'un QCM passent par le même helper KaTeX',
    ],
    resoudre_qcm_unique: {
        support: 'ax + b = cx + d, a de 2 à 9, c de 1 à 8 différent de a, b de ±1 à ±9, solution entière x0 de −5 à 5 non nulle',
        familles: {
            oppose: '−x0 : le signe de la solution perdu',
            signe_constante: '(d + b)/(a − c) : b passé de l\'autre côté sans changer de signe (« chacun rentre chez soi » mal fait)',
            signe_inconnue: '(d − b)/(a + c) : cx passé de l\'autre côté sans changer de signe',
            membre_droit_sans_x: '(d − b)/a : le cx du membre de droite oublié',
            division_inversee: '(a − c)/(d − b) : la division à l\'envers ; vaut 1/x0, jamais entière hors x0 = ±1 où elle vaut la clé : présente au réservoir, jamais servie',
        },
        garantie: 'trois distracteurs tirés parmi les valeurs ENTIÈRES du réservoir, distinctes, différentes de x0, de valeur absolue au plus 12 ; nouveau tirage complet (300 essais, environ 14 % des tirages conviennent) sinon, puis repli vérifié 4x + 2 = 2x + 8 ; des distracteurs entiers, une clé entière parmi des fractions serait repérable',
        residuel: 'À ARBITRER : oppose servie dans 97 % des tirages, la clé est alors dans l\'unique paire x0, −x0 des choix (85 %) ; la grille oppose × signe_constante supprimerait la paire unique mais mettrait la clé au milieu des choix triés (75 %)',
    },
    mathieu_qcm_unique: {
        support: '« Mathieu affirme que 2,67 est solution de 3x = 8 » : a parmi 3, 6, 7, 9, 11, c de 2 à 22 premier avec a et non multiple de a ; approximation au centième, jamais exacte',
        familles: {
            inversion: 'a/c au lieu de c/a (nom de 1049, 1107)',
            soustraction: 'c − a quand c > a : 3x = 8 lu 8 − 3 (nom de 1049, 1107)',
            operation_directe: 'a × c quand c < a : l\'opération refaite au lieu d\'être défaite (1107)',
            approchee_prise_pour_exacte: '« Oui : 2,67 est la solution exacte. » : la valeur approchée prise pour la solution',
        },
        moule: 'quatre options de même forme (« Non : la solution exacte est … » trois fois, « Oui : … est la solution exacte. » une fois) ; la clé ne dit plus « valeur approchée »',
    },
    inverse_qcm_unique: {
        support: 'ax = c (c multiple de a), −ax = c, x + b = c, x − b = c ; toutes les options disent « aux deux membres »',
        familles: {
            operation_directe: 'l\'opération de l\'équation refaite (multiplier par a pour ax = c, ajouter b pour x + b = c)',
            inversion: 'diviser par c au lieu de a (ax = c)',
            les_deux: 'multiplier par c (ax = c) ; multiplier par b (x ± b = c)',
            signe: 'diviser par a au lieu de −a (−ax = c)',
            lecture_additive: '−ax lu −a + x : ajouter a (−ax = c)',
            lecture_multiplicative: 'x + b lu b × x : diviser par b (x ± b = c)',
        },
        grille: 'ax = c : verbe (diviser, multiplier) × nombre (a, c), la clé n\'est plus la seule à partager son verbe et son nombre avec les autres',
    },
    verifier_tableau_vf: 'trois lignes « La solution de … est … » (prof 2026-09-24 : quatre, c\'était trop long), une ou deux vraies ; au moins une fausse de famille nommée (oppose −x0, signe_constante (d + b)/(a − c)) ; les autres fausses : famille nommée ou voisin (x0 ± 1, ± 2)',
};

// ═════════ Assemblage ═════════

const GENERATORS = {
    resoudre_texte: genResoudreSaisie,
    resoudre_guide_texte_trou_saisie: genResoudreGuide,
    resoudre_qcm_unique: genResoudreQcm,
    etapes_ordonner: genEtapesOrdonner,
    fraction_texte_trou_saisie: genFractionSaisie,
    fraction_guide_texte_trou_saisie: genFractionGuide,
    mathieu_qcm_unique: genMathieuQcm,
    inverse_qcm_unique: genInverseQcm,
    verifier_tableau_vf: genVerifierVf,
};

const FORMAT_LABELS = {
    qcm_unique: 'QCM', tableau_vf: 'Vrai/Faux', texte: 'Saisie', texte_trou_saisie: 'Saisie trous',
    guide_texte_trou_saisie: 'Saisie guidée', ordonner: 'Ordonner',
};
const buildFormats = (prefix) =>
    Object.keys(GENERATORS).filter(k => k.startsWith(`${prefix}_`))
        .map(k => ({ key: k, label: FORMAT_LABELS[k.slice(prefix.length + 1)] || k }));

// etapes : nombre d'étapes de raisonnement de la carte, validé par le prof le
// 2026-10-06 (chantier arbres-4e) ; sert aux exercices complexes du groupe Acquis.
export const CATEGORIES = [
    { id: 'resoudre', label: L_RESOUDRE, etapes: 3, formatsList: [...buildFormats('resoudre'), ...buildFormats('etapes')] },
    { id: 'fraction', label: L_FRACTION, etapes: 2, formatsList: [...buildFormats('fraction'), ...buildFormats('mathieu')] },
    { id: 'inverse', label: L_INVERSE, etapes: 1, formatsList: buildFormats('inverse') },
    { id: 'verifier', label: L_VERIFIER, etapes: 2, formatsList: buildFormats('verifier') },
];

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) key = typeExercice;
    else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter(k => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else key = randomChoice(Object.keys(GENERATORS));
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};
